/**
 * Error thrown by sharePointDriveService for a non-ok Graph response, carrying the HTTP status
 * as a real field (not just baked into the message) so callers — notably the withRetry layer —
 * can branch on 429/503 (throttling/transient) vs 404/403 (fatal) without string-parsing.
 *
 * The message intentionally keeps the existing `<message> (<status>)` shape so existing tests
 * that assert on the thrown text keep passing.
 */
export class GraphRequestError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    /** Parsed from a Retry-After header when present, in milliseconds. */
    public readonly retryAfterMs?: number,
    /**
     * Graph's own error `code` from the response body, when it was read — e.g.
     * 'InvalidAuthenticationToken', 'MailboxNotEnabledForRESTAPI', 'ErrorAccessDenied'.
     * Populated only by graphErrorWithDetail below.
     */
    public readonly graphCode?: string,
    public readonly graphInnerCode?: string,
  ) {
    super(`${message} (${status})`);
    this.name = 'GraphRequestError';
  }
}

/**
 * Parses a Retry-After header into milliseconds. Graph sends either an integer number of seconds
 * or an HTTP-date. Returns undefined when absent/unparseable. Guards against a missing `headers`
 * object because the service test's `jsonResponse` stub historically omits it.
 */
export function parseRetryAfterMs(
  headers: Headers | undefined,
): number | undefined {
  const raw = headers?.get?.('Retry-After');
  if (!raw) return undefined;

  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);

  const dateMs = Date.parse(raw);
  if (Number.isFinite(dateMs)) return Math.max(0, dateMs - Date.now());

  return undefined;
}

/** Factory: builds a GraphRequestError from a message + the failed Response. */
export function graphError(
  message: string,
  response: Response,
): GraphRequestError {
  return new GraphRequestError(
    message,
    response.status,
    parseRetryAfterMs(response.headers),
  );
}

interface GraphErrorBody {
  error?: {
    code?: string;
    message?: string;
    innererror?: { code?: string };
    innerError?: { code?: string };
  };
}

/**
 * Async sibling of `graphError` that also READS the error body, so Graph's own `code` and `message`
 * survive into the console and onto the error object.
 *
 * Worth the extra await because a bare status is not enough to act on for the auth-shaped failures:
 * a 401 alone cannot distinguish "token isn't a Graph token" from "token expired" from
 * "Bearer undefined", while `InvalidAuthenticationToken` + Graph's message says exactly which.
 *
 * No double-read hazard: this is only ever called on the failure path, where nothing else consumes the
 * body, and `graphError`'s callers only touch `response.headers`. Every read is defensive — a body that
 * is empty, non-JSON, or a test stub without `json()` must still produce a usable error rather than
 * throwing something unclassifiable from inside a catch block.
 */
export async function graphErrorWithDetail(
  message: string,
  response: Response,
): Promise<GraphRequestError> {
  let code: string | undefined;
  let innerCode: string | undefined;
  let detail: string | undefined;

  try {
    const body = (await response.json?.()) as GraphErrorBody | undefined;
    code = body?.error?.code;
    // Graph spells it `innererror`; some surfaces send `innerError`.
    innerCode = body?.error?.innererror?.code ?? body?.error?.innerError?.code;
    detail = body?.error?.message;
  } catch {
    // Empty or non-JSON body — the status alone still has to be reportable.
  }

  // Logged rather than folded into the message: the message shape `<message> (<status>)` is asserted by
  // tests and read by users, while this is for whoever is looking at a console during triage.
  console.error(
    `Graph request failed: ${message} (${response.status})`,
    code ? `code=${code}` : '',
    innerCode ? `innerCode=${innerCode}` : '',
    detail ?? '',
  );

  return new GraphRequestError(
    message,
    response.status,
    parseRetryAfterMs(response.headers),
    code,
    innerCode,
  );
}
